/**
 * Validate a control's Referrals / Open Dental mapping before the Control
 * Configuration sidebar is allowed to save.
 *
 * WHY THIS IS A SAVE GATE, NOT A HINT
 * "Referrals" is a WRITE-BACK target: on submission the forms-service turns the
 * control's stored `[{text, value}]` into an INSERT/UPDATE against Open Dental's
 * `refattach` table, where `value` must be a `ReferralNum`. Two mismatches would
 * store a config that can never do what the author intended, with no error at
 * submit time:
 *
 *   1. Mapping some OTHER control (an input, or an opendentaldata control still
 *      set to DISEASES/ALLERGIES/MEDICATIONS) to Referrals. Its value is free
 *      text, or a DiseaseDefNum / AllergyDefNum / MedicationNum — never a
 *      ReferralNum. The write-back would be skipped or point at a random
 *      referral source.
 *   2. Mapping a REFERRALS control to Allergies / Diseases / Medications. Its
 *      ReferralNums would be sent down the allergy/disease/medication write
 *      path and attach unrelated chart entries to the patient.
 *
 * So the two must agree in BOTH directions, and the sidebar refuses the save
 * rather than storing a config that is silently inert or actively wrong.
 *
 * IMPORT-FREE ON PURPOSE: cypress/node-tests/forms-od-referral-mapping.test.js
 * loads THIS file's source, strips the export and evaluates it, so the tests
 * always exercise the shipped code instead of a mirror that can drift.
 *
 * @param {Object} control - The control being configured (may be null/undefined)
 * @returns {string} '' when the mapping is coherent, else the message to show.
 */
function getOdReferralMappingError(control) {
    if (!control || typeof control !== 'object') {
        return '';
    }

    const mapping = control.teamioODColumn === null || control.teamioODColumn === undefined
        ? ''
        : String(control.teamioODColumn).trim();
    const isReferralMapping = mapping === 'Referrals';

    const isOpenDentalDataControl = control.type === 'opendentaldata';
    const dataType = control.dataType === null || control.dataType === undefined
        ? ''
        : String(control.dataType).trim().toUpperCase();
    const isReferralDataType = isOpenDentalDataControl && dataType === 'REFERRALS';

    // Rule 1 — saving to Referrals requires an Open Dental Data Field set to REFERRALS.
    if (isReferralMapping && !isReferralDataType) {
        return 'To save to Referrals in Open Dental, select Referrals from the Select Open Dental Data Type dropdown.';
    }

    // Rule 2 — a REFERRALS field may only be left unmapped or mapped to Referrals.
    if (isReferralDataType && mapping !== '' && !isReferralMapping) {
        return 'A Referrals field can only be saved to the Referrals Open Dental field.';
    }

    return '';
}

export { getOdReferralMappingError };
